import { useEffect, useRef, useState } from 'react';
import { MapPinIcon, WrenchIcon } from '@heroicons/react/24/outline';
import UserGroupIcon from '../../../../../public/assets/Icons-Users.svg';
import { ToggleButton } from '@/shared/components/ToggleButton';
import { Menu, MenuVariant, MenuSize } from '@/shared/components/Menu';
import type { FacilityListItem } from '../../types/facilityListType';
import { FACILITY_LABELS } from '../../constants/facilityLabels';

interface FacilityListProps {
  facilities: FacilityListItem[];
  loading: boolean;
  error: string | null;
  onMenuAction?:
    | ((action: string, facility: FacilityListItem) => void)
    | undefined;
  onToggle?: ((facility: FacilityListItem) => void) | undefined;
}

const DotsVerticalIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface FacilityCardProps {
  facility: FacilityListItem;
  onMenuAction?:
    | ((action: string, facility: FacilityListItem) => void)
    | undefined;
  onToggle?: ((facility: FacilityListItem) => void) | undefined;
  index?: number;
}

const FacilityCard = ({
  facility,
  onMenuAction,
  onToggle,
  index = 0,
}: FacilityCardProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const isActive = facility.status === 'active';

  return (
    <div className="border border-[#DCDDE0] rounded-xl px-4 py-4 flex items-center gap-4">
      {/* Location icon */}
      <div className="shrink-0 text-[#474A4C] rounded-full bg-[#F2F4F5] p-2">
        <MapPinIcon className="w-6 h-6" color="#878B8E" />
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <p className="text-bodyMedium text-[#181A1B] wrap-break-word">
          {facility.name} [{facility.facilityId}]
        </p>
        <div className="flex items-center gap-4 text-[#474A4C]">
          <span className="flex items-center gap-1 text-bodySmall text-[#878B8E]">
            <WrenchIcon className="w-4 h-4" color="#878B8E" />
            {facility.equipmentCount} {FACILITY_LABELS.SECTION_EQUIPMENT}
          </span>
          <span className="flex items-center gap-1 text-bodySmall text-[#878B8E]">
            <UserGroupIcon width={16} height={16} />
            {facility.facilityUserCount} {FACILITY_LABELS.SECTION_FACILITY_USER}
          </span>
          <span className="text-bodySmall text-[#878B8E]">
            {FACILITY_LABELS.CREATED_DATE_LABEL}:{' '}
            {new Date(facility.createdAt)
              .toLocaleDateString('pl-PL', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })
              .replace(/\./g, '/')
              .split('/')
              .reverse()
              .join('/')}
          </span>
        </div>
      </div>

      {/* Toggle + menu */}
      <div className="shrink-0 flex items-center gap-3">
        {/* Outer div handles the click to open confirmation dialog.
            The ToggleButton has pointer-events-none so its internal handleClick
            never fires optimistically — the checked prop only changes after API
            success. The inner wrapper div also has pointerEvents:none so clicks
            on the non-button area within it still reach the outer div. */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.(facility);
          }}
          className="cursor-pointer inline-flex"
          data-testid={`toggle-status-${index}`}
        >
          <div style={{ pointerEvents: 'none' }}>
            <ToggleButton
              checked={isActive}
              onChange={() => {
                // no-op: toggle state updates only after confirmed API success
              }}
              label={isActive ? 'Aktywny' : 'Nieaktywny'}
              size="Small"
              className="pointer-events-none"
              labelClassName="inline-block w-20 text-left"
            />
          </div>
        </div>

        <div ref={menuAnchorRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            data-testid={`facility-menu-button-${index}`}
            aria-label={`Akcje dla placówki ${facility.name}`}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <DotsVerticalIcon />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 z-50">
              <Menu
                items={[{ id: 'EDIT', label: 'Edytuj' }]}
                variant={MenuVariant.Light}
                size={MenuSize.Small}
                toolTip={false}
                onItemClick={(itemId) => {
                  setMenuOpen(false);
                  onMenuAction?.(itemId, facility);
                }}
                showLeadingIcons={false}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const FacilityList = ({
  facilities,
  loading,
  error,
  onMenuAction,
  onToggle,
}: FacilityListProps) => {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        {error && (
          <p className="text-sm text-[#E11B1B] leading-5 tracking-[0.25px]">
            {error}
          </p>
        )}

        {loading ? (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="border border-[#DCDDE0] rounded-2xl px-4 py-3 h-[68px] animate-pulse bg-[#F5F5F5]"
              />
            ))}
          </div>
        ) : facilities.length === 0 && !error ? (
          <p className="text-bodyLarge text-[#474A4C]">
            Brak placówek do wyświetlenia.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {facilities.map((facility, i) => (
              <FacilityCard
                key={facility.id}
                facility={facility}
                onMenuAction={onMenuAction}
                onToggle={onToggle}
                index={i}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
